import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import env from '../config/env.js';
import { buildAbsoluteUrl, canonicalOrigin } from '../utils/canonicalUrl.js';
import usePublicSettings from './usePublicSettings.js';

// Shipped fallback card (client/public/og-default.png, 1200x630) so every page
// has a share image even before an admin uploads a Default OG Image.
const BUNDLED_OG_IMAGE = '/og-default.png';

// An empty value REMOVES the tag. Bailing out instead would leave the previous
// route's value in the head - in an SPA that means a page advertising another
// page's canonical URL.
function upsertMeta(name, content, { property = false } = {}) {
  const attr = property ? 'property' : 'name';
  const selector = `meta[${attr}="${name}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el) el.remove();
    return null;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el) el.remove();
    return null;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

// Lightweight SEO hook. Sets document.title + key meta tags imperatively.
//
// SEO priority (per page): page-specific value first, then the admin "Default
// SEO" settings, then the hardcoded app-name fallback. A page that passes its
// own title/description always wins; pages that pass nothing fall back to the
// settings defaults.
export default function useSeo({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  // Alt text for the share image, and the og:type for the page ('website',
  // 'product', 'article'). Both are optional with sensible defaults.
  ogImageAlt,
  ogType = 'website',
  noindex,
  nofollow,
  robots,
  // When true the title is applied verbatim (already a fully-resolved title,
  // e.g. from the backend SEO resolver) instead of being suffixed with the site
  // name. Pages passing a short label leave this false to get "Label | Site".
  exactTitle = false,
} = {}) {
  const { settings } = usePublicSettings();
  const { pathname } = useLocation();

  const siteName = settings.siteName || env.APP_NAME;
  const defTitle = settings.defaultMetaTitle || '';
  const defDescription = settings.defaultMetaDescription || '';
  const defOgImage = settings.defaultOgImageUrl || '';
  const canonicalBase = canonicalOrigin(settings.siteCanonicalUrl);

  useEffect(() => {
    // Resolved titles are applied verbatim; short page labels get "Label | Site".
    // With no title at all, fall back to the default meta title, then site name.
    const fullTitle = title
      ? exactTitle
        ? title
        : `${title} | ${siteName}`
      : defTitle || siteName;
    document.title = fullTitle;

    const effDescription = description || defDescription;
    // Page image, then the admin default, then the bundled card. Resolved to an
    // absolute URL either way - crawlers reject relative og:image values.
    const effOgImage = buildAbsoluteUrl(
      ogImage || defOgImage || BUNDLED_OG_IMAGE,
      canonicalBase
    );
    // Canonical: the explicit page value when there is one, else the current
    // path. Either way it is resolved against the canonical origin, so the tag
    // always names the preferred host - never the www/apex variant the visitor
    // happened to type, and never a stale value from the previous route.
    const effCanonical = buildAbsoluteUrl(canonical || pathname, canonicalBase);

    const effTitle = ogTitle || title || defTitle || siteName;
    const isBundledImage = effOgImage.endsWith(BUNDLED_OG_IMAGE);

    // Explicit robots wins; otherwise derive the full index/follow matrix.
    const robotsValue =
      robots ||
      `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`;

    [
      upsertMeta('description', effDescription),
      upsertMeta('robots', robotsValue),
      upsertMeta('og:title', effTitle, { property: true }),
      upsertMeta('og:description', ogDescription || effDescription, { property: true }),
      upsertMeta('og:image', effOgImage, { property: true }),
      upsertMeta('og:image:alt', ogImageAlt || effTitle, { property: true }),
      // index.html declares 1200x630 for the bundled card. Once a page swaps in
      // its own image those dimensions are wrong, so they are dropped unless the
      // bundled card is still what's being advertised.
      upsertMeta('og:image:width', isBundledImage ? '1200' : '', { property: true }),
      upsertMeta('og:image:height', isBundledImage ? '630' : '', { property: true }),
      upsertMeta('og:url', effCanonical, { property: true }),
      upsertMeta('og:type', ogType, { property: true }),
      upsertMeta('og:site_name', siteName, { property: true }),
      // X/Twitter reads its own namespace and ignores most og:* tags.
      upsertMeta('twitter:card', effOgImage ? 'summary_large_image' : 'summary'),
      upsertMeta('twitter:title', effTitle),
      upsertMeta('twitter:description', ogDescription || effDescription),
      upsertMeta('twitter:image', effOgImage),
      upsertLink('canonical', effCanonical),
    ].filter(Boolean);
  }, [
    title,
    description,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogImageAlt,
    ogType,
    noindex,
    nofollow,
    robots,
    exactTitle,
    siteName,
    defTitle,
    defDescription,
    defOgImage,
    canonicalBase,
    pathname,
  ]);
}
